import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

interface ApiCampaign {
  id: string;
  campaign_id: string;
  profile_id: string;
  name: string;
  state: string;
  daily_budget: number;
  budget_type: string;
  targeting_type: string;
  dynamic_bidding: {
    strategy: string;
    placementBidding: Array<{ percentage: number; placement: string }>;
  };
  off_amazon_settings?: Record<string, any>;
  tags?: Record<string, any>;
  start_date: string;
  end_date?: string | null;
  raw_data?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export function CampaignDetails() {
  const { id } = useParams<{ id: string }>();
  console.log('[CampaignDetails] useParams id:', id);

  const {
    data: campaign,
    isLoading,
    isError,
    error,
  } = useQuery<ApiCampaign, Error>({
    queryKey: ['campaign', id],
    queryFn: async () => {
      console.log('[CampaignDetails] Fetching campaign for id:', id);
      const res = await api.get(`/api/v1/campaigns/${id}`);
      console.log('[CampaignDetails] API response:', res);
      // The API returns { success, data }, so we want res.data.data
      if (!res.data || typeof res.data !== 'object') {
        throw new Error('Invalid API response');
      }
      if (!('data' in res.data)) {
        throw new Error('API response missing data property');
      }
      console.log('[CampaignDetails] Fetched campaign data:', res.data.data);
      return res.data.data;
    },
    enabled: !!id,
  });
  console.log('[CampaignDetails] Query state:', { isLoading, isError, error, campaign });

  if (isLoading) {
    console.log('[CampaignDetails] Render: Loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
          <p className="mt-4 text-foreground-muted">Loading campaign details...</p>
        </div>
      </div>
    );
  }
  if (isError) {
    console.log('[CampaignDetails] Render: Error state', error);
    return <div className="p-8 text-center text-error">Error: {error?.message || 'Unknown error.'}</div>;
  }
  if (!campaign) {
    console.log('[CampaignDetails] Render: No campaign found');
    return <div className="p-8 text-center">No campaign found.</div>;
  }

  console.log('[CampaignDetails] Render: Campaign details', campaign);
  return (
    <div className="max-w-3xl mx-auto p-8 bg-surface rounded-xl shadow-lg mt-10">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{campaign?.name || 'Unnamed Campaign'}</h1>
          <div className="text-foreground-subtle text-sm">Campaign ID: <span className="font-mono">{campaign?.campaign_id || 'N/A'}</span></div>
        </div>
        <div className="mt-4 md:mt-0">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-surface-muted border border-border mr-2 ${campaign?.state === 'ENABLED' ? 'text-success-ink border-success/30' : campaign?.state === 'PAUSED' ? 'text-warning-ink border-yellow-300' : 'text-foreground-subtle'}`}>{campaign?.state || 'UNKNOWN'}</span>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-brand-subtle text-brand border border-brand/20">{campaign?.targeting_type || 'N/A'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-page rounded-xl p-4">
          <div className="font-semibold text-foreground-muted mb-1">Budget</div>
          <div className="text-lg font-bold text-foreground">
            {(() => {
              const val = campaign?.daily_budget;
              console.log('[CampaignDetails] Render: daily_budget', val);
              return typeof val === 'number' && !isNaN(val)
                ? `$${val.toLocaleString()}`
                : 'N/A';
            })()}{' '}
            <span className="text-xs font-normal text-foreground-subtle">({campaign?.budget_type || 'N/A'})</span>
          </div>
        </div>
        <div className="bg-page rounded-xl p-4">
          <div className="font-semibold text-foreground-muted mb-1">Bidding Strategy</div>
          <div className="text-foreground">{(() => {
            const strat = campaign?.dynamic_bidding?.strategy;
            console.log('[CampaignDetails] Render: dynamic_bidding.strategy', strat);
            return strat || 'N/A';
          })()}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-page rounded-xl p-4">
          <div className="font-semibold text-foreground-muted mb-1">Start Date</div>
          <div className="text-foreground">{(() => {
            const start = campaign?.start_date;
            console.log('[CampaignDetails] Render: start_date', start);
            return start || 'N/A';
          })()}</div>
        </div>
        <div className="bg-page rounded-xl p-4">
          <div className="font-semibold text-foreground-muted mb-1">End Date</div>
          <div className="text-foreground">{(() => {
            const end = campaign?.end_date;
            console.log('[CampaignDetails] Render: end_date', end);
            return end || 'N/A';
          })()}</div>
        </div>
      </div>

      <div className="bg-page rounded-xl p-4 mb-8">
        <div className="font-semibold text-foreground-muted mb-1">Meta</div>
        <div className="text-xs text-foreground-subtle">Profile ID: <span className="font-mono">{(() => {
          const pid = campaign?.profile_id;
          console.log('[CampaignDetails] Render: profile_id', pid);
          return pid || 'N/A';
        })()}</span></div>
        <div className="text-xs text-foreground-subtle">Created: {(() => {
          const created = campaign?.created_at;
          console.log('[CampaignDetails] Render: created_at', created);
          if (created) {
            try { return new Date(created).toLocaleString(); } catch (e) { console.log('[CampaignDetails] Error parsing created_at', e); return 'N/A'; }
          }
          return 'N/A';
        })()}</div>
        <div className="text-xs text-foreground-subtle">Updated: {(() => {
          const updated = campaign?.updated_at;
          console.log('[CampaignDetails] Render: updated_at', updated);
          if (updated) {
            try { return new Date(updated).toLocaleString(); } catch (e) { console.log('[CampaignDetails] Error parsing updated_at', e); return 'N/A'; }
          }
          return 'N/A';
        })()}</div>
      </div>

      {/* Advanced: Placement Bidding Table if present */}
      {Array.isArray(campaign?.dynamic_bidding?.placementBidding) && campaign.dynamic_bidding.placementBidding.length > 0 && (() => {
        console.log('[CampaignDetails] Render: Placement Bidding Table', campaign.dynamic_bidding.placementBidding);
        return (
          <div className="bg-page rounded-xl p-4 mb-8">
            <div className="font-semibold text-foreground-muted mb-2">Placement Bidding</div>
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-foreground-subtle">
                  <th className="pr-4 py-1">Placement</th>
                  <th className="pr-4 py-1">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {campaign.dynamic_bidding.placementBidding.map((pb, idx) => {
                  console.log('[CampaignDetails] Render: placementBidding row', idx, pb);
                  return (
                    <tr key={idx} className="border-t border-border">
                      <td className="pr-4 py-1">{pb?.placement || 'N/A'}</td>
                      <td className="pr-4 py-1">{typeof pb?.percentage === 'number' ? `${pb.percentage}%` : 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Raw Data (for debugging) */}
      {/* <pre className="bg-surface-muted rounded p-4 text-xs overflow-x-auto mt-8">{JSON.stringify(campaign, null, 2)}</pre> */}
    </div>
  );
}
